import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Calendar, Users, PoundSterling, Plus, Edit, Trash2, CheckCircle, 
  XCircle, Clock, MapPin, ArrowLeft, Eye, RefreshCcw, Image, UserPlus, Star, X,
  User, Phone, Mail, AlertTriangle, ClipboardList, CreditCard, ExternalLink, Search, Loader2, Gift, Link2, MessageCircle, Zap
} from 'lucide-react';
import Navbar from '../components/Navbar';
import ManualBootcampBookingModal from '../components/bootcamp/ManualBootcampBookingModal';
import api from '../lib/api';


const UpcomingInstallmentsPanel = ({ api }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const fetchUpcoming = async () => {
    setLoading(true);
    try {
      const res = await api.get('/bootcamps/installments/upcoming');
      setRows(res.data?.upcoming || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load instalments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUpcoming();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const overdue = rows.filter((r) => r.is_overdue);
  const dueSoon = rows.filter((r) => !r.is_overdue);
  const totalNextDue = rows.reduce((s, r) => s + Number(r.next_due_amount || 0), 0);

  return (
    <Card className="bg-neutral-900 border-neutral-800">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-white flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-amber-400" />
            Upcoming Camp Instalments
          </CardTitle>
          <p className="text-white/60 text-sm mt-1">
            All active instalment plans. Use this to track who owes what and when.
          </p>
        </div>
        <Button onClick={fetchUpcoming} disabled={loading} variant="outline" size="sm" className="border-neutral-700 text-white" data-testid="refresh-installments-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
        </Button>
      </CardHeader>
      <CardContent>
        {/* Summary */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-neutral-950 rounded-lg p-3 border border-neutral-800">
            <p className="text-white/50 text-xs">Active plans</p>
            <p className="text-white text-xl font-bold">{rows.length}</p>
          </div>
          <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/30">
            <p className="text-red-400 text-xs">Overdue</p>
            <p className="text-red-400 text-xl font-bold">{overdue.length}</p>
          </div>
          <div className="bg-amber-500/10 rounded-lg p-3 border border-amber-500/30">
            <p className="text-amber-400 text-xs">Next dues total</p>
            <p className="text-amber-400 text-xl font-bold">£{totalNextDue.toFixed(2)}</p>
          </div>
        </div>

        {loading && rows.length === 0 && (
          <div className="text-center py-12 text-white/50">
            <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" /> Loading instalments…
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div className="text-center py-12 text-white/50">
            <CreditCard className="w-12 h-12 mx-auto mb-2 opacity-40" />
            <p>No active instalment plans right now.</p>
          </div>
        )}

        {rows.length > 0 && (
          <div className="overflow-x-auto rounded border border-neutral-800">
            <table className="w-full text-sm">
              <thead className="bg-neutral-950 text-white/60">
                <tr>
                  <th className="text-left p-2">Student</th>
                  <th className="text-left p-2">Camp</th>
                  <th className="text-left p-2">Parent</th>
                  <th className="text-right p-2">Next due</th>
                  <th className="text-right p-2">Amount</th>
                  <th className="text-right p-2">Plan total</th>
                  <th className="text-right p-2"></th>
                </tr>
              </thead>
              <tbody>
                {[...overdue, ...dueSoon].map((r) => (
                  <FragmentRow key={r.plan_id} row={r} expanded={!!expanded[r.plan_id]} toggle={() => setExpanded((s) => ({ ...s, [r.plan_id]: !s[r.plan_id] }))} today={today} onCharged={fetchUpcoming} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};


const FragmentRow = ({ row, expanded, toggle, today, onCharged }) => {
  const [chargingPlanInst, setChargingPlanInst] = useState(null); // installment_number being charged

  const chargeInstallment = async (installment_number) => {
    if (!confirm(`Charge ${row.student_name} £${Number(row.next_due_amount || 0).toFixed(2)} now?`)) return;
    setChargingPlanInst(installment_number);
    try {
      const res = await api.post(`/bootcamps/installments/${row.plan_id}/charge/${installment_number}`);
      if (res.data?.already_paid) {
        toast.info('That instalment is already marked paid');
      } else {
        toast.success(`Charged £${Number(res.data?.amount_charged || 0).toFixed(2)} ✓`);
      }
      if (onCharged) onCharged();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Failed to charge';
      toast.error(`Charge failed — ${detail}`);
    } finally {
      setChargingPlanInst(null);
    }
  };

  return (
    <>
      <tr className={`border-t border-neutral-800 ${row.is_overdue ? 'bg-red-500/5' : ''}`} data-testid={`installment-row-${row.plan_id}`}>
        <td className="p-2 text-white">{row.student_name || '—'}</td>
        <td className="p-2 text-white/80">{row.bootcamp_name}</td>
        <td className="p-2 text-white/70">
          <div>{row.parent_name}</div>
          <div className="text-xs text-white/50">{row.parent_email}</div>
        </td>
        <td className="p-2 text-right">
          <span className={row.is_overdue ? 'text-red-400 font-semibold' : 'text-white'}>
            {row.next_due_date}
            {row.is_overdue && <span className="ml-1 text-xs">(overdue)</span>}
          </span>
        </td>
        <td className="p-2 text-right text-pink-300 font-semibold">£{Number(row.next_due_amount || 0).toFixed(2)}</td>
        <td className="p-2 text-right text-white/70">£{Number(row.total_amount || 0).toFixed(2)}</td>
        <td className="p-2 text-right">
          <div className="flex items-center justify-end gap-1">
            {(row.is_overdue || (row.next_due_status === 'failed')) && (
              <Button
                size="sm"
                onClick={() => {
                  const next = (row.installments || []).find(i => i.status !== 'paid');
                  if (!next) return;
                  chargeInstallment(next.installment_number);
                }}
                disabled={chargingPlanInst !== null}
                className="bg-emerald-600 hover:bg-emerald-700 h-7 px-2 text-xs"
                data-testid={`charge-now-${row.plan_id}`}
              >
                {chargingPlanInst !== null ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Charge now'}
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={toggle} className="text-amber-400 hover:text-amber-300 h-7 px-2 text-xs">
              {expanded ? 'Hide' : 'Schedule'}
            </Button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="border-t border-neutral-800 bg-neutral-950/50">
          <td colSpan={7} className="p-3">
            <div className="space-y-1">
              {row.installments.map((inst, i) => {
                const isUnpaid = inst.status !== 'paid';
                const isPastDue = isUnpaid && (inst.due_date || '') <= today;
                return (
                  <div key={i} className="flex justify-between text-xs">
                    <span className="text-white/70">
                      #{inst.installment_number || i + 1} · {inst.due_date}
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="text-white">£{Number(inst.amount || 0).toFixed(2)}</span>
                      <span
                        className={
                          inst.status === 'paid'
                            ? 'text-emerald-400'
                            : inst.status === 'failed'
                              ? 'text-red-400'
                              : (inst.due_date || '') < today
                                ? 'text-red-400'
                                : 'text-amber-400'
                        }
                      >
                        [{inst.status}]
                      </span>
                      {isPastDue && (
                        <button
                          onClick={() => chargeInstallment(inst.installment_number)}
                          disabled={chargingPlanInst !== null}
                          className="text-emerald-400 hover:text-emerald-300 underline disabled:opacity-50"
                          data-testid={`charge-inst-${row.plan_id}-${inst.installment_number}`}
                        >
                          {chargingPlanInst === inst.installment_number ? 'Charging…' : 'charge'}
                        </button>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};


const BootcampAdmin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [bootcamps, setBootcamps] = useState([]);
  const [manualBookOpen, setManualBookOpen] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [bookingFilter, setBookingFilter] = useState('all'); // 'all', 'pending', 'confirmed', 'cancelled'
  
  // Modal states
  const [bootcampModal, setBootcampModal] = useState(false);
  const [editingBootcamp, setEditingBootcamp] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [studentProfile, setStudentProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // Payment reconciliation modal
  const [reconcileBooking, setReconcileBooking] = useState(null);
  const [reconcileForm, setReconcileForm] = useState({
    amount_paid: '', installment_total: '', installments_paid: '',
    next_installment_date: '', next_installment_amount: '',
    stripe_payment_intent_id: '', note: '',
  });
  const [reconcileSaving, setReconcileSaving] = useState(false);

  // "Add Sibling" admin shortcut state
  const [siblingFor, setSiblingFor] = useState(null); // existing booking
  const [siblingForm, setSiblingForm] = useState({
    student_name: '', student_age: '',
    medical_info: '',
    mark_as_paid: false, paid_amount: '', paid_note: '',
  });
  const [siblingSaving, setSiblingSaving] = useState(false);

  const openSiblingModal = (booking) => {
    setSiblingFor(booking);
    setSiblingForm({
      student_name: '', student_age: '',
      medical_info: '',
      mark_as_paid: false, paid_amount: '', paid_note: '',
    });
  };

  const handleAddSibling = async () => {
    if (!siblingFor) return;
    if (!siblingForm.student_name.trim() || !siblingForm.student_age) {
      toast.error('Sibling name and age are required');
      return;
    }
    setSiblingSaving(true);
    try {
      const payload = {
        existing_booking_id: siblingFor.id,
        student_name: siblingForm.student_name.trim(),
        student_age: parseInt(siblingForm.student_age, 10),
        medical_info: siblingForm.medical_info || undefined,
        mark_as_paid: !!siblingForm.mark_as_paid,
        paid_amount: siblingForm.paid_amount !== '' ? parseFloat(siblingForm.paid_amount) : undefined,
        paid_note: siblingForm.paid_note || undefined,
      };
      const res = await api.post('/bootcamps/admin/add-sibling-to-booking', payload);
      const sib = res.data?.sibling_discount_info;
      toast.success(
        sib
          ? `${res.data.student_name} added at £${Number(res.data.amount).toFixed(2)} (30% sibling discount applied)`
          : `${res.data.student_name} added at £${Number(res.data.amount).toFixed(2)} (full price — first sibling not yet paid)`,
      );
      setSiblingFor(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add sibling');
    } finally {
      setSiblingSaving(false);
    }
  };

  const openReconcileModal = (booking) => {
    setReconcileBooking(booking);
    setReconcileForm({
      amount_paid: booking.amount_paid != null ? String(booking.amount_paid) : '',
      installment_total: booking.installment_total != null ? String(booking.installment_total) : '',
      installments_paid: booking.installments_paid != null ? String(booking.installments_paid) : '',
      next_installment_date: (booking.next_installment_date || '').slice(0, 10),
      next_installment_amount: booking.next_installment_amount != null ? String(booking.next_installment_amount) : '',
      stripe_payment_intent_id: booking.stripe_payment_intent_id || '',
      note: '',
    });
  };

  const submitReconcile = async () => {
    if (!reconcileBooking) return;
    setReconcileSaving(true);
    try {
      const payload = {};
      if (reconcileForm.amount_paid !== '') payload.amount_paid = Number(reconcileForm.amount_paid);
      if (reconcileForm.installment_total !== '') payload.installment_total = Number(reconcileForm.installment_total);
      if (reconcileForm.installments_paid !== '') payload.installments_paid = Number(reconcileForm.installments_paid);
      if (reconcileForm.next_installment_date) payload.next_installment_date = reconcileForm.next_installment_date;
      if (reconcileForm.next_installment_amount !== '') payload.next_installment_amount = Number(reconcileForm.next_installment_amount);
      if (reconcileForm.stripe_payment_intent_id !== '') payload.stripe_payment_intent_id = reconcileForm.stripe_payment_intent_id.trim();
      if (reconcileForm.note) payload.note = reconcileForm.note;
      const res = await api.patch(`/bootcamps/bookings/${reconcileBooking.id}/payment`, payload);
      toast.success(`${reconcileBooking.student_name}'s payment reconciled`);
      const sibs = res.data?.siblings_discounted || [];
      if (sibs.length > 0) {
        const total_saved = sibs.reduce((s, x) => s + (x.saved || 0), 0);
        toast.success(
          `30% sibling discount auto-applied to ${sibs.length} sibling${sibs.length > 1 ? 's' : ''} (£${total_saved.toFixed(2)} saved): ${sibs.map(x => x.student_name).filter(Boolean).join(', ')}`,
          { duration: 8000 }
        );
      }
      setReconcileBooking(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reconcile');
    } finally {
      setReconcileSaving(false);
    }
  };
  
  // Attendance/Register state
  const [selectedRegisterBootcamp, setSelectedRegisterBootcamp] = useState(null);
  const [registerData, setRegisterData] = useState(null);
  const [selectedRegisterDate, setSelectedRegisterDate] = useState(null);
  const [registerLoading, setRegisterLoading] = useState(false);
  
  // Form state
  const [bootcampForm, setBootcampForm] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    price: 275,
    capacity: 30,
    age_min: 6,
    age_max: 18,
    location: 'UPA Studios, Milton Keynes',
    image_url: '',
    features: [],
    what_to_bring: [],
    guest_teachers: [],
    daily_schedule: '',
    is_active: true
  });
  
  // Guest teacher form
  const [guestTeacherForm, setGuestTeacherForm] = useState({
    name: '',
    role: '',
    bio: '',
    image_url: '',
    credits: []
  });
  const [newCredit, setNewCredit] = useState('');
  const [newFeature, setNewFeature] = useState('');
  const [newWhatToBring, setNewWhatToBring] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [bootcampsRes, bookingsRes, statsRes] = await Promise.all([
        api.get('/bootcamps?include_past=true'),
        api.get('/bootcamps/bookings/all'),
        api.get('/bootcamps/dashboard/stats')
      ]);
      setBootcamps(bootcampsRes.data);
      setBookings(bookingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load bootcamp data');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Open WhatsApp with a pre-filled message + payment link.
  // Strips non-digit characters from the phone, defaults to UK (+44) if a
  // local 07... number is provided. If no phone on file, falls back to a
  // generic web.whatsapp.com share link.
  const handleWhatsAppMessage = (booking) => {
    const url = `${window.location.origin}/bootcamp-pay/${booking.id}`;
    const studentFirst = (booking.student_name || 'your child').split(' ')[0];
    const camp = booking.bootcamp_name || 'camp';
    const amt = Number(booking.balance_owed || booking.amount || 0).toFixed(2);
    const message = `Hi! Just a quick note about ${studentFirst}'s place at ${camp}. There's £${amt} left to settle whenever you're ready — you can sort it here (pay in full or instalments, both use your saved card): ${url}`;

    let phone = (booking.parent_phone || '').replace(/\D/g, '');
    if (phone.startsWith('0')) phone = '44' + phone.slice(1);
    if (phone.startsWith('44') === false && phone.length === 10) phone = '44' + phone;

    const waUrl = phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
      : `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(waUrl, '_blank', 'noopener,noreferrer');
  };

  // Resend the Pay-now / Instalments balance email for an `awaiting_balance` booking.
  const handleResendBalanceEmail = async (booking) => {
    try {
      const r = await api.post(`/bootcamps/bookings/${booking.id}/resend-balance-email`);
      if (r.data?.success) {
        toast.success(r.data.message || `Balance email sent to ${booking.parent_email}`);
      } else {
        const err = r.data?.error || 'unknown';
        toast.error(`Email failed: ${err}. Use "Copy link" to send via WhatsApp.`);
        console.error('Resend failed', r.data);
      }
      fetchData();
    } catch (err) {
      console.error('Resend error', err);
      toast.error(err.response?.data?.detail || err.message || 'Failed to resend email');
    }
  };

  // Copy the parent-facing /bootcamp-pay URL to clipboard with iOS/Safari fallback.
  const handleCopyPaymentLink = async (booking) => {
    const url = `${window.location.origin}/bootcamp-pay/${booking.id}`;
    let copied = false;
    try {
      await navigator.clipboard.writeText(url);
      copied = true;
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        copied = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch { copied = false; }
    }
    if (copied) {
      toast.success('Payment link copied — paste into WhatsApp / SMS');
    } else {
      window.prompt('Copy this link:', url);
    }
  };

  const handleCreateBootcamp = async () => {
    try {
      if (editingBootcamp) {
        await api.put(`/bootcamps/${editingBootcamp.id}`, bootcampForm);
        toast.success('Bootcamp updated successfully');
      } else {
        await api.post('/bootcamps', bootcampForm);
        toast.success('Bootcamp created successfully');
      }
      setBootcampModal(false);
      setEditingBootcamp(null);
      resetBootcampForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save bootcamp');
    }
  };

  const handleDeleteBootcamp = async (bootcampId) => {
    if (!window.confirm('Are you sure you want to delete this bootcamp?')) return;
    
    try {
      await api.delete(`/bootcamps/${bootcampId}`);
      toast.success('Bootcamp deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete bootcamp');
    }
  };

  const handleUpdateBookingStatus = async (bookingId, status) => {
    try {
      await api.put(`/bootcamps/bookings/${bookingId}/status?status=${status}`);
      toast.success(`Booking ${status}`);
      fetchData();
      setSelectedBooking(null);
    } catch (error) {
      toast.error('Failed to update booking status');
    }
  };

  // Delete a booking
  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to permanently delete this registration? This cannot be undone.')) return;
    
    try {
      await api.delete(`/bootcamps/bookings/${bookingId}`);
      toast.success('Registration deleted');
      fetchData();
      setSelectedBooking(null);
      setStudentProfile(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete registration');
    }
  };

  // Request payment for a booking
  const [paymentLink, setPaymentLink] = useState(null);
  const [paymentLinkBooking, setPaymentLinkBooking] = useState(null);
  
  const handleRequestPayment = async (booking) => {
    try {
      const res = await api.post(`/bootcamps/bookings/${booking.id}/request-payment`);
      const url = res.data.checkout_url;
      setPaymentLink(url);
      setPaymentLinkBooking(booking);
      
      // Try to copy to clipboard
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Payment link copied to clipboard!');
      } catch {
        // Clipboard failed - dialog will show the link
        toast.info('Payment link generated — copy from the dialog below');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payment link');
    }
  };


  // Load student profile
  const loadStudentProfile = async (bookingId) => {
    setProfileLoading(true);
    try {
      const res = await api.get(`/bootcamps/bookings/${bookingId}/profile`);
      setStudentProfile(res.data);
    } catch (error) {
      toast.error('Failed to load student profile');
    } finally {
      setProfileLoading(false);
    }
  };

  // Load bootcamp register
  const loadRegister = async (bootcampId, date = null) => {
    setRegisterLoading(true);
    try {
      const url = date 
        ? `/bootcamps/${bootcampId}/register?date=${date}`
        : `/bootcamps/${bootcampId}/register`;
      const res = await api.get(url);
      setRegisterData(res.data);
      if (date) {
        setSelectedRegisterDate(date);
      }
    } catch (error) {
      toast.error('Failed to load register');
    } finally {
      setRegisterLoading(false);
    }
  };

  // Mark attendance
  const markAttendance = async (bookingId, present) => {
    if (!selectedRegisterBootcamp || !selectedRegisterDate) return;
    
    try {
      await api.post(`/bootcamps/${selectedRegisterBootcamp}/register/mark`, {
        booking_id: bookingId,
        date: selectedRegisterDate,
        present: present
      });
      toast.success(present ? 'Marked as present' : 'Marked as absent');
      loadRegister(selectedRegisterBootcamp, selectedRegisterDate);
    } catch (error) {
      toast.error('Failed to mark attendance');
    }
  };

  const resetBootcampForm = () => {
    setBootcampForm({
      name: '',
      description: '',
      start_date: '',
      end_date: '',
      price: 275,
      capacity: 30,
      age_min: 6,
      age_max: 18,
      location: 'UPA Studios, Milton Keynes',
      image_url: '',
      features: [],
      what_to_bring: [],
      guest_teachers: [],
      daily_schedule: '',
      is_active: true
    });
    setGuestTeacherForm({ name: '', role: '', bio: '', image_url: '', credits: [] });
  };

  const openEditModal = (bootcamp) => {
    setEditingBootcamp(bootcamp);
    setBootcampForm({
      name: bootcamp.name,
      description: bootcamp.description || '',
      start_date: bootcamp.start_date,
      end_date: bootcamp.end_date,
      price: bootcamp.price,
      capacity: bootcamp.capacity,
      age_min: bootcamp.age_min,
      age_max: bootcamp.age_max,
      location: bootcamp.location,
      image_url: bootcamp.image_url || '',
      features: bootcamp.features || [],
      what_to_bring: bootcamp.what_to_bring || [],
      guest_teachers: bootcamp.guest_teachers || [],
      daily_schedule: bootcamp.daily_schedule || '',
      is_active: bootcamp.is_active
    });
    setBootcampModal(true);
  };

  // Guest teacher helpers
  const addGuestTeacher = () => {
    if (!guestTeacherForm.name) {
      toast.error('Please enter a name for the guest teacher');
      return;
    }
    setBootcampForm({
      ...bootcampForm,
      guest_teachers: [...bootcampForm.guest_teachers, { ...guestTeacherForm }]
    });
    setGuestTeacherForm({ name: '', role: '', bio: '', image_url: '', credits: [] });
  };

  const removeGuestTeacher = (index) => {
    setBootcampForm({
      ...bootcampForm,
      guest_teachers: bootcampForm.guest_teachers.filter((_, i) => i !== index)
    });
  };

  const addCreditToTeacher = () => {
    if (!newCredit.trim()) return;
    setGuestTeacherForm({
      ...guestTeacherForm,
      credits: [...guestTeacherForm.credits, newCredit.trim()]
    });
    setNewCredit('');
  };

  const addFeature = () => {
    if (!newFeature.trim()) return;
    setBootcampForm({
      ...bootcampForm,
      features: [...bootcampForm.features, newFeature.trim()]
    });
    setNewFeature('');
  };

  const removeFeature = (index) => {
    setBootcampForm({
      ...bootcampForm,
      features: bootcampForm.features.filter((_, i) => i !== index)
    });
  };

  const addWhatToBring = () => {
    if (!newWhatToBring.trim()) return;
    setBootcampForm({
      ...bootcampForm,
      what_to_bring: [...bootcampForm.what_to_bring, newWhatToBring.trim()]
    });
    setNewWhatToBring('');
  };

  const removeWhatToBring = (index) => {
    setBootcampForm({
      ...bootcampForm,
      what_to_bring: bootcampForm.what_to_bring.filter((_, i) => i !== index)
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      confirmed: 'bg-green-500/20 text-green-400',
      cancelled: 'bg-red-500/20 text-red-400',
      attended: 'bg-blue-500/20 text-blue-400',
      no_show: 'bg-gray-500/20 text-gray-400'
    };
    return <Badge className={styles[status] || styles.pending}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="animate-spin w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/admin')}
              className="text-white/70 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-3xl font-bold">
                West End <span className="text-purple-500">Bootcamps</span>
              </h1>
              <p className="text-white/60">Manage bootcamp events and registrations</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setManualBookOpen(true)}
              variant="outline"
              className="border-purple-500 text-purple-300 hover:bg-purple-500/10"
              data-testid="open-manual-book-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add student
            </Button>
            <Button
              onClick={() => { resetBootcampForm(); setEditingBootcamp(null); setBootcampModal(true); }}
              className="bg-purple-500 hover:bg-purple-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Bootcamp
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card 
            className="bg-neutral-900 border-neutral-800 cursor-pointer hover:border-purple-500/50 transition-all"
            onClick={() => setActiveTab('overview')}
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <p className="text-white/50 text-sm">Active Bootcamps</p>
                  <p className="text-2xl font-bold text-white">{stats?.active_bootcamps || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-neutral-900 border-neutral-800 cursor-pointer hover:border-green-500/50 transition-all"
            onClick={() => { setBookingFilter('all'); setActiveTab('bookings'); }}
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-white/50 text-sm">Total Bookings</p>
                  <p className="text-2xl font-bold text-white">{stats?.total_bookings || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-neutral-900 border-neutral-800 cursor-pointer hover:border-yellow-500/50 transition-all"
            onClick={() => { setBookingFilter('pending'); setActiveTab('bookings'); }}
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-400" />
                </div>
                <div>
                  <p className="text-white/50 text-sm">Pending</p>
                  <p className="text-2xl font-bold text-white">{stats?.pending_bookings || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-neutral-900 border-neutral-800">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <PoundSterling className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <p className="text-white/50 text-sm">Revenue</p>
                  <p className="text-2xl font-bold text-white">£{stats?.total_revenue?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-neutral-900 border-neutral-800 mb-6">
            <TabsTrigger 
              value="overview" 
              className="data-[state=active]:bg-purple-500"
              data-testid="bootcamp-tab-overview"
            >
              Bootcamps
            </TabsTrigger>
            <TabsTrigger 
              value="bookings" 
              className="data-[state=active]:bg-purple-500"
              data-testid="bootcamp-tab-bookings"
            >
              Registrations ({bookings.length})
            </TabsTrigger>
            <TabsTrigger 
              value="students" 
              className="data-[state=active]:bg-purple-500"
              data-testid="bootcamp-tab-students"
            >
              Students
            </TabsTrigger>
            <TabsTrigger 
              value="register" 
              className="data-[state=active]:bg-purple-500"
              data-testid="bootcamp-tab-register"
            >
              Attendance
            </TabsTrigger>
            <TabsTrigger 
              value="installments" 
              className="data-[state=active]:bg-amber-500"
              data-testid="bootcamp-tab-installments"
            >
              Instalments
            </TabsTrigger>
          </TabsList>

          {/* Bootcamps Tab */}
          <TabsContent value="overview">
            {bootcamps.length === 0 ? (
              <Card className="bg-neutral-900 border-neutral-800">
                <CardContent className="p-12 text-center">
                  <Calendar className="w-16 h-16 text-white/20 mx-auto mb-4" />
                  <p className="text-white/50 text-lg">No bootcamps created yet</p>
                  <p className="text-white/30 mt-2">Create your first West End bootcamp to get started</p>
                  <Button 
                    onClick={() => setBootcampModal(true)}
                    className="mt-6 bg-purple-500 hover:bg-purple-600"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Bootcamp
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {bootcamps.map((bootcamp) => (
                  <Card 
                    key={bootcamp.id} 
                    className="bg-neutral-900 border-neutral-800 cursor-pointer hover:border-purple-500/50 transition-all"
                    onClick={() => navigate(`/admin/bootcamps/${bootcamp.id}`)}
                  >
                    {/* Cover Image or gradient bar */}
                    {bootcamp.image_url ? (
                      <div className="h-32 overflow-hidden">
                        <img 
                          src={bootcamp.image_url} 
                          alt={bootcamp.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className={`h-2 ${bootcamp.is_active ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-neutral-700'}`} />
                    )}
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <h3 className="font-bold text-white text-lg">{bootcamp.name}</h3>
                        {!bootcamp.is_active && <Badge className="bg-red-500/20 text-red-400">Inactive</Badge>}
                      </div>
                      
                      <div className="space-y-2 mb-4 text-sm text-white/70">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-purple-400" />
                          {new Date(bootcamp.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - {new Date(bootcamp.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-purple-400" />
                          {bootcamp.location}
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-purple-400" />
                          Ages {bootcamp.age_min}-{bootcamp.age_max} • {bootcamp.spots_remaining}/{bootcamp.capacity} spots
                        </div>
                        <div className="flex items-center gap-2">
                          <PoundSterling className="w-4 h-4 text-purple-400" />
                          £{bootcamp.price} per child
                        </div>
                      </div>
                      
                      {/* Guest teachers preview */}
                      {bootcamp.guest_teachers && bootcamp.guest_teachers.length > 0 && (
                        <div className="mb-4 pb-4 border-b border-neutral-800">
                          <p className="text-xs text-purple-400 mb-2">{bootcamp.guest_teachers.length} Guest Teacher{bootcamp.guest_teachers.length > 1 ? 's' : ''}</p>
                          <div className="flex -space-x-2">
                            {bootcamp.guest_teachers.slice(0, 3).map((teacher, idx) => (
                              teacher.image_url ? (
                                <img 
                                  key={idx}
                                  src={teacher.image_url} 
                                  alt={teacher.name}
                                  className="w-8 h-8 rounded-full border-2 border-neutral-900 object-cover"
                                />
                              ) : (
                                <div 
                                  key={idx}
                                  className="w-8 h-8 rounded-full border-2 border-neutral-900 bg-purple-500/30 flex items-center justify-center"
                                >
                                  <Star className="w-4 h-4 text-purple-400" />
                                </div>
                              )
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          className="flex-1 bg-purple-500 hover:bg-purple-600"
                          onClick={(e) => { e.stopPropagation(); navigate(`/admin/bootcamps/${bootcamp.id}`); }}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          View & Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={(e) => { e.stopPropagation(); handleDeleteBootcamp(bootcamp.id); }}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            {/* Filter Buttons */}
            <div className="flex gap-2 mb-4 flex-wrap">
              <Button
                size="sm"
                variant={bookingFilter === 'all' ? 'default' : 'outline'}
                onClick={() => setBookingFilter('all')}
                className={bookingFilter === 'all' ? 'bg-purple-500' : 'border-neutral-700'}
              >
                All ({bookings.length})
              </Button>
              <Button
                size="sm"
                variant={bookingFilter === 'pending' ? 'default' : 'outline'}
                onClick={() => setBookingFilter('pending')}
                className={bookingFilter === 'pending' ? 'bg-yellow-500' : 'border-neutral-700'}
              >
                Pending ({bookings.filter(b => b.status === 'pending').length})
              </Button>
              <Button
                size="sm"
                variant={bookingFilter === 'confirmed' ? 'default' : 'outline'}
                onClick={() => setBookingFilter('confirmed')}
                className={bookingFilter === 'confirmed' ? 'bg-green-500' : 'border-neutral-700'}
              >
                Confirmed ({bookings.filter(b => b.status === 'confirmed').length})
              </Button>
              <Button
                size="sm"
                variant={bookingFilter === 'cancelled' ? 'default' : 'outline'}
                onClick={() => setBookingFilter('cancelled')}
                className={bookingFilter === 'cancelled' ? 'bg-red-500' : 'border-neutral-700'}
              >
                Cancelled ({bookings.filter(b => b.status === 'cancelled').length})
              </Button>

              <div className="ml-auto">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-pink-500/40 text-pink-300 hover:bg-pink-500/10"
                  data-testid="recompute-sibling-discounts-btn"
                  onClick={async () => {
                    if (!confirm('Re-apply the 30% sibling discount across all active camp bookings? Eldest sibling pays full price, every younger sibling gets 30% off. Paid bookings are untouched.')) return;
                    try {
                      const res = await api.post('/bootcamps/admin/recompute-sibling-discounts', {});
                      const n = res.data?.bookings_updated || 0;
                      if (n === 0) {
                        toast.success('All sibling discounts already correct — nothing to update.');
                      } else {
                        toast.success(`${n} booking${n > 1 ? 's' : ''} updated with sibling discount`);
                      }
                      fetchData();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to recompute sibling discounts');
                    }
                  }}
                >
                  Recalculate sibling discounts
                </Button>
              </div>
            </div>

            {bookings.length === 0 ? (
              <Card className="bg-neutral-900 border-neutral-800">
                <CardContent className="p-12 text-center">
                  <Users className="w-16 h-16 text-white/20 mx-auto mb-4" />
                  <p className="text-white/50 text-lg">No registrations yet</p>
                  <p className="text-white/30 mt-2">Bookings will appear here when parents register</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-neutral-900 border-neutral-800">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-neutral-800">
                          <th className="text-left p-4 text-white/50 font-medium">Student</th>
                          <th className="text-left p-4 text-white/50 font-medium">Bootcamp</th>
                          <th className="text-left p-4 text-white/50 font-medium">Parent Contact</th>
                          <th className="text-left p-4 text-white/50 font-medium">Amount</th>
                          <th className="text-left p-4 text-white/50 font-medium">Status</th>
                          <th className="text-left p-4 text-white/50 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bookings
                          .filter(b => bookingFilter === 'all' || b.status === bookingFilter)
                          .map((booking) => (
                          <tr key={booking.id} className="border-b border-neutral-800/50 hover:bg-neutral-800/30">
                            <td className="p-4">
                              <div>
                                <p className="font-medium text-white">{booking.student_name}</p>
                                <p className="text-sm text-white/50">Age {booking.student_age}</p>
                              </div>
                            </td>
                            <td className="p-4 text-white/70">{booking.bootcamp_name}</td>
                            <td className="p-4">
                              <div className="text-sm">
                                <p className="text-white">{booking.parent_name}</p>
                                <p className="text-white/50">{booking.parent_email}</p>
                                <p className="text-white/50">{booking.parent_phone}</p>
                              </div>
                            </td>
                            <td className="p-4 font-medium text-white">
                              <div className="flex flex-col">
                                <span>£{Number(booking.amount || 0).toFixed(2)}</span>
                                {booking.sibling_discount_info && (
                                  <span className="text-[10px] text-pink-400 mt-0.5" data-testid={`sibling-discount-${booking.id}`}>
                                    Sibling 30% off (saved £{Number(booking.sibling_discount_info.amount_off || 0).toFixed(2)})
                                  </span>
                                )}
                                {(booking.amount_paid !== undefined && Number(booking.amount_paid) > 0) && (
                                  <span className="text-xs text-emerald-400" data-testid={`amount-paid-${booking.id}`}>
                                    Paid £{Number(booking.amount_paid).toFixed(2)}
                                    {booking.installment_total > 0 && (
                                      <> &nbsp;({booking.installments_paid || 0}/{booking.installment_total})</>
                                    )}
                                  </span>
                                )}
                                {booking.payment_status === 'paid' && (
                                  <span className="text-xs text-emerald-400">Paid in full ✓</span>
                                )}
                                {booking.payment_status === 'awaiting_balance' && (
                                  <span className="text-xs text-amber-300" data-testid={`awaiting-balance-${booking.id}`}>
                                    Awaiting balance £{Number(booking.balance_owed || booking.amount || 0).toFixed(2)}
                                    {booking.deposit_paid ? ` (deposit £${Number(booking.deposit_paid).toFixed(2)} paid)` : ''}
                                  </span>
                                )}
                                {booking.payment_status === 'partial' && booking.next_installment_date && (
                                  <span className="text-xs text-amber-400">
                                    Next due {String(booking.next_installment_date).slice(0,10)}
                                    {booking.next_installment_amount ? ` £${Number(booking.next_installment_amount).toFixed(2)}` : ''}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="p-4">{getStatusBadge(booking.status)}</td>
                            <td className="p-4">
                              <div className="flex gap-2 flex-wrap">
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => setSelectedBooking(booking)}
                                  className="text-white/70 hover:text-white"
                                  title="View details"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => openReconcileModal(booking)}
                                  className="text-emerald-400 hover:text-emerald-300"
                                  title="Reconcile payment"
                                  data-testid={`reconcile-payment-${booking.id}`}
                                >
                                  <PoundSterling className="w-4 h-4" />
                                </Button>
                                {booking.payment_status === 'awaiting_balance' && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleWhatsAppMessage(booking)}
                                      className="text-green-400 hover:text-green-300"
                                      title="Send via WhatsApp (opens WhatsApp with message + link pre-filled)"
                                      data-testid={`whatsapp-balance-${booking.id}`}
                                    >
                                      <MessageCircle className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleCopyPaymentLink(booking)}
                                      className="text-cyan-400 hover:text-cyan-300"
                                      title="Copy payment link"
                                      data-testid={`copy-payment-link-${booking.id}`}
                                    >
                                      <Link2 className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleResendBalanceEmail(booking)}
                                      className="text-pink-400 hover:text-pink-300"
                                      title="Email Pay-now / Instalments link to parent"
                                      data-testid={`resend-balance-email-${booking.id}`}
                                    >
                                      <Mail className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                                {booking.stripe_payment_intent_id ? (
                                  <a
                                    href={`https://dashboard.stripe.com/payments/${booking.stripe_payment_intent_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center w-9 h-9 rounded text-purple-400 hover:text-purple-300 hover:bg-white/5"
                                    title="View in Stripe"
                                    data-testid={`view-stripe-${booking.id}`}
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </a>
                                ) : booking.parent_email ? (
                                  <a
                                    href={`https://dashboard.stripe.com/search?query=${encodeURIComponent(booking.parent_email)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center w-9 h-9 rounded text-white/40 hover:text-white/70 hover:bg-white/5"
                                    title="Find in Stripe (search by parent email)"
                                  >
                                    <Search className="w-4 h-4" />
                                  </a>
                                ) : null}
                                {booking.status === 'pending' && (
                                  <>
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      onClick={() => handleUpdateBookingStatus(booking.id, 'confirmed')}
                                      className="text-green-400 hover:text-green-300"
                                      title="Confirm"
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      onClick={() => handleUpdateBookingStatus(booking.id, 'cancelled')}
                                      className="text-red-400 hover:text-red-300"
                                      title="Cancel"
                                    >
                                      <XCircle className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                                {booking.payment_status !== 'paid' && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={async () => {
                                      if (!window.confirm(`Mark ${booking.student_name} as COMP (free, £0)?`)) return;
                                      try {
                                        await api.post(`/bootcamps/bookings/${booking.id}/mark-comp`, { note: 'Comp — staff/family' });
                                        toast.success(`${booking.student_name} marked as comp (registered, £0).`);
                                        fetchData();
                                      } catch (err) {
                                        toast.error(err.response?.data?.detail || 'Failed to mark comp');
                                      }
                                    }}
                                    className="text-emerald-400 hover:text-emerald-300"
                                    title="Mark as Comp (free, no charge)"
                                    data-testid={`mark-comp-${booking.id}`}
                                  >
                                    <Gift className="w-4 h-4" />
                                  </Button>
                                )}
                                {booking.payment_status !== 'paid' && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={async () => {
                                      const remaining = (booking.amount || 0) - (booking.amount_paid || 0);
                                      const input = window.prompt(
                                        `Amount to charge ${booking.parent_name || 'the parent'}'s saved card for ${booking.student_name}.\nFull remaining: £${remaining.toFixed(2)} — enter a lower amount to pro-rate for partial days.`,
                                        remaining.toFixed(2)
                                      );
                                      if (input === null) return;
                                      const amt = parseFloat(input);
                                      if (!amt || amt <= 0) { toast.error('Invalid amount'); return; }
                                      try {
                                        const res = await api.post(`/bootcamps/bookings/${booking.id}/charge-saved-card`, { amount: (booking.amount_paid || 0) + amt });
                                        toast.success(`Charged £${res.data.amount_charged?.toFixed(2)} — ${booking.student_name}`);
                                        fetchData();
                                      } catch (err) {
                                        toast.error(err.response?.data?.detail || 'Charge failed');
                                      }
                                    }}
                                    className="text-purple-400 hover:text-purple-300"
                                    title="Charge parent's saved card now"
                                    data-testid={`charge-saved-card-${booking.id}`}
                                  >
                                    <Zap className="w-4 h-4" />
                                  </Button>
                                )}
                                {booking.payment_status !== 'paid' && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    onClick={() => handleRequestPayment(booking)}
                                    className="text-amber-400 hover:text-amber-300"
                                    title="Copy payment link"
                                    data-testid={`request-payment-${booking.id}`}
                                  >
                                    <CreditCard className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openSiblingModal(booking)}
                                  className="text-pink-400 hover:text-pink-300"
                                  title="Add sibling to this booking"
                                  data-testid={`add-sibling-to-${booking.id}`}
                                >
                                  <UserPlus className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleDeleteBooking(booking.id)}
                                  className="text-red-400 hover:text-red-300"
                                  title="Delete permanently"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Students Tab */}
          <TabsContent value="students">
            <Card className="bg-neutral-900 border-neutral-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-400" />
                  Bootcamp Students
                </CardTitle>
              </CardHeader>
              <CardContent>
                {bookings.filter(b => b.status !== 'cancelled').length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-16 h-16 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50">No students registered yet</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {bookings.filter(b => b.status !== 'cancelled').map(booking => (
                      <Card 
                        key={booking.id} 
                        className="bg-neutral-800 border-neutral-700 hover:border-purple-500/50 cursor-pointer transition-all"
                        onClick={() => { loadStudentProfile(booking.id); }}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center">
                              <User className="w-6 h-6 text-purple-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-white truncate">{booking.student_name}</p>
                              <p className="text-sm text-white/50">Age {booking.student_age}</p>
                              <p className="text-xs text-purple-400 mt-1 truncate">{booking.bootcamp_name}</p>
                            </div>
                            {getStatusBadge(booking.status)}
                          </div>
                          <div className="mt-3 pt-3 border-t border-neutral-700 text-sm">
                            <p className="text-white/70 truncate"><Mail className="w-3 h-3 inline mr-1" />{booking.parent_email}</p>
                            <p className="text-white/70 truncate"><Phone className="w-3 h-3 inline mr-1" />{booking.parent_phone}</p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Attendance/Register Tab */}
          <TabsContent value="register">
            <Card className="bg-neutral-900 border-neutral-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-purple-400" />
                  Bootcamp Attendance Register
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Bootcamp Selector */}
                <div className="mb-6">
                  <Label className="text-white/70 mb-2 block">Select Bootcamp</Label>
                  <Select
                    value={selectedRegisterBootcamp || ''}
                    onValueChange={(value) => {
                      setSelectedRegisterBootcamp(value);
                      setSelectedRegisterDate(null);
                      loadRegister(value);
                    }}
                  >
                    <SelectTrigger className="w-full bg-neutral-800 border-neutral-700 text-white">
                      <SelectValue placeholder="Choose a bootcamp..." />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-800 border-neutral-700">
                      {bootcamps.map(bc => (
                        <SelectItem key={bc.id} value={bc.id} className="text-white">
                          {bc.name} ({bc.start_date} - {bc.end_date})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {registerLoading && (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full" />
                  </div>
                )}

                {!registerLoading && registerData && !selectedRegisterDate && (
                  <div>
                    <p className="text-white/70 mb-4">
                      Total enrolled: <span className="text-white font-medium">{registerData.total_enrolled}</span> students
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
                      {registerData.dates?.map(d => (
                        <Card 
                          key={d.date}
                          className="bg-neutral-800 border-neutral-700 hover:border-purple-500 cursor-pointer transition-all"
                          onClick={() => loadRegister(selectedRegisterBootcamp, d.date)}
                        >
                          <CardContent className="p-4 text-center">
                            <p className="text-lg font-bold text-white">{d.day_name}</p>
                            <p className="text-sm text-white/50">{d.date}</p>
                            <div className="mt-2 flex justify-center gap-2 text-xs">
                              <span className="text-green-400">{d.present} ✓</span>
                              <span className="text-red-400">{d.absent} ✗</span>
                              <span className="text-white/40">{d.unmarked} ?</span>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {!registerLoading && registerData && selectedRegisterDate && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <Button
                          variant="ghost"
                          onClick={() => { setSelectedRegisterDate(null); loadRegister(selectedRegisterBootcamp); }}
                          className="text-white/70 hover:text-white mb-2"
                        >
                          <ArrowLeft className="w-4 h-4 mr-2" /> Back to dates
                        </Button>
                        <h3 className="text-xl font-bold text-white">{registerData.bootcamp_name}</h3>
                        <p className="text-white/50">{selectedRegisterDate}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-white/50">
                          Present: <span className="text-green-400 font-medium">{registerData.present}</span> / {registerData.total}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {registerData.students?.map(student => (
                        <div 
                          key={student.booking_id}
                          className="flex items-center justify-between p-4 bg-neutral-800 rounded-lg border border-neutral-700"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              student.present === true ? 'bg-green-500/20' :
                              student.present === false ? 'bg-red-500/20' : 'bg-neutral-700'
                            }`}>
                              <span className={`font-medium ${
                                student.present === true ? 'text-green-400' :
                                student.present === false ? 'text-red-400' : 'text-white/50'
                              }`}>
                                {student.student_name?.charAt(0)?.toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-white">{student.student_name}</p>
                              <p className="text-sm text-white/50">Age {student.student_age}</p>
                              {student.medical_info && (
                                <p className="text-xs text-yellow-400 mt-1">
                                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                                  {student.medical_info}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant={student.present === true ? 'default' : 'outline'}
                              onClick={() => markAttendance(student.booking_id, true)}
                              className={student.present === true ? 'bg-green-500 hover:bg-green-600' : 'border-green-500/50 text-green-400'}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" /> Present
                            </Button>
                            <Button
                              size="sm"
                              variant={student.present === false ? 'default' : 'outline'}
                              onClick={() => markAttendance(student.booking_id, false)}
                              className={student.present === false ? 'bg-red-500 hover:bg-red-600' : 'border-red-500/50 text-red-400'}
                            >
                              <XCircle className="w-4 h-4 mr-1" /> Absent
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!selectedRegisterBootcamp && (
                  <div className="text-center py-12">
                    <ClipboardList className="w-16 h-16 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50">Select a bootcamp to view the attendance register</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Instalments Tab */}
          <TabsContent value="installments">
            <UpcomingInstallmentsPanel api={api} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Student Profile Modal */}
      <Dialog open={!!studentProfile} onOpenChange={() => setStudentProfile(null)}>
        <DialogContent className="sm:max-w-lg bg-neutral-950 border-neutral-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-purple-400" />
              Student Profile
            </DialogTitle>
          </DialogHeader>
          
          {profileLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full" />
            </div>
          ) : studentProfile && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-neutral-900 rounded-lg">
                <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{studentProfile.student_name}</h3>
                  <p className="text-white/50">Age {studentProfile.student_age}</p>
                  <Badge className="mt-1">{studentProfile.bootcamp_name}</Badge>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="text-sm font-medium text-purple-400 uppercase tracking-wider">Parent/Guardian Contact</h4>
                <div className="p-4 bg-neutral-900 rounded-lg space-y-2">
                  <p className="text-white"><User className="w-4 h-4 inline mr-2 text-white/50" />{studentProfile.parent_name}</p>
                  <p className="text-white"><Mail className="w-4 h-4 inline mr-2 text-white/50" />{studentProfile.parent_email}</p>
                  <p className="text-white"><Phone className="w-4 h-4 inline mr-2 text-white/50" />{studentProfile.parent_phone}</p>
                </div>
              </div>

              {studentProfile.emergency_contact_name && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-purple-400 uppercase tracking-wider">Emergency Contact</h4>
                  <div className="p-4 bg-neutral-900 rounded-lg space-y-2">
                    <p className="text-white"><User className="w-4 h-4 inline mr-2 text-white/50" />{studentProfile.emergency_contact_name}</p>
                    <p className="text-white"><Phone className="w-4 h-4 inline mr-2 text-white/50" />{studentProfile.emergency_contact_phone}</p>
                  </div>
                </div>
              )}

              {studentProfile.medical_info && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-purple-400 uppercase tracking-wider">Medical</h4>
                  <div className="p-4 bg-neutral-900 rounded-lg space-y-2">
                    <p className="text-yellow-400"><AlertTriangle className="w-4 h-4 inline mr-2" />{studentProfile.medical_info}</p>
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-4 border-t border-neutral-800">
                <Button
                  variant="outline"
                  className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
                  onClick={() => {
                    handleDeleteBooking(studentProfile.id);
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete Registration
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setStudentProfile(null)}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Bootcamp Modal */}
      <Dialog open={bootcampModal} onOpenChange={setBootcampModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] bg-neutral-950 border-neutral-800 text-white">
          <DialogHeader>
            <DialogTitle>{editingBootcamp ? 'Edit Bootcamp' : 'Create New Bootcamp'}</DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="max-h-[70vh] pr-4">
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-purple-400 uppercase tracking-wider">Basic Information</h3>
                
                <div className="space-y-2">
                  <Label>Bootcamp Name *</Label>
                  <Input
                    value={bootcampForm.name}
                    onChange={e => setBootcampForm({...bootcampForm, name: e.target.value})}
                    className="bg-neutral-900 border-neutral-700"
                    placeholder="e.g., West End Summer Bootcamp 2026"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Description *</Label>
                  <Textarea
                    value={bootcampForm.description}
                    onChange={e => setBootcampForm({...bootcampForm, description: e.target.value})}
                    className="bg-neutral-900 border-neutral-700 min-h-[100px]"
                    placeholder="Describe the bootcamp experience..."
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Cover Image URL</Label>
                  <Input
                    value={bootcampForm.image_url}
                    onChange={e => setBootcampForm({...bootcampForm, image_url: e.target.value})}
                    className="bg-neutral-900 border-neutral-700"
                    placeholder="https://example.com/image.jpg"
                  />
                  {bootcampForm.image_url && (
                    <div className="mt-2 rounded-lg overflow-hidden h-32 bg-neutral-800">
                      <img 
                        src={bootcampForm.image_url} 
                        alt="Preview" 
                        className="w-full h-full object-cover"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>
              </div>
              
              {/* Dates & Pricing */}
              <div className="space-y-4 border-t border-neutral-800 pt-4">
                <h3 className="text-sm font-medium text-purple-400 uppercase tracking-wider">Dates & Pricing</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date *</Label>
                    <Input
                      type="date"
                      value={bootcampForm.start_date}
                      onChange={e => setBootcampForm({...bootcampForm, start_date: e.target.value})}
                      className="bg-neutral-900 border-neutral-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date *</Label>
                    <Input
                      type="date"
                      value={bootcampForm.end_date}
                      onChange={e => setBootcampForm({...bootcampForm, end_date: e.target.value})}
                      className="bg-neutral-900 border-neutral-700"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Price (£) *</Label>
                    <Input
                      type="number"
                      value={bootcampForm.price}
                      onChange={e => setBootcampForm({...bootcampForm, price: parseFloat(e.target.value) || 0})}
                      className="bg-neutral-900 border-neutral-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Capacity</Label>
                    <Input
                      type="number"
                      value={bootcampForm.capacity}
                      onChange={e => setBootcampForm({...bootcampForm, capacity: parseInt(e.target.value) || 30})}
                      className="bg-neutral-900 border-neutral-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Input
                      value={bootcampForm.location}
                      onChange={e => setBootcampForm({...bootcampForm, location: e.target.value})}
                      className="bg-neutral-900 border-neutral-700"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Min Age</Label>
                    <Input
                      type="number"
                      value={bootcampForm.age_min}
                      onChange={e => setBootcampForm({...bootcampForm, age_min: parseInt(e.target.value) || 6})}
                      className="bg-neutral-900 border-neutral-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Age</Label>
                    <Input
                      type="number"
                      value={bootcampForm.age_max}
                      onChange={e => setBootcampForm({...bootcampForm, age_max: parseInt(e.target.value) || 18})}
                      className="bg-neutral-900 border-neutral-700"
                    />
                  </div>
                </div>
              </div>
              
              {/* Features */}
              <div className="space-y-4 border-t border-neutral-800 pt-4">
                <h3 className="text-sm font-medium text-purple-400 uppercase tracking-wider">Features & Highlights</h3>
                
                <div className="flex gap-2">
                  <Input
                    value={newFeature}
                    onChange={e => setNewFeature(e.target.value)}
                    className="bg-neutral-900 border-neutral-700 flex-1"
                    placeholder="e.g., Professional West End Training"
                    onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                  />
                  <Button type="button" onClick={addFeature} size="sm" className="bg-purple-500 hover:bg-purple-600">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                
                {bootcampForm.features.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {bootcampForm.features.map((feature, idx) => (
                      <Badge key={idx} className="bg-purple-500/20 text-purple-300 px-3 py-1">
                        {feature}
                        <button onClick={() => removeFeature(idx)} className="ml-2 hover:text-white">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              
              {/* What to Bring */}
              <div className="space-y-4 border-t border-neutral-800 pt-4">
                <h3 className="text-sm font-medium text-purple-400 uppercase tracking-wider">What to Bring</h3>
                
                <div className="flex gap-2">
                  <Input
                    value={newWhatToBring}
                    onChange={e => setNewWhatToBring(e.target.value)}
                    className="bg-neutral-900 border-neutral-700 flex-1"
                    placeholder="e.g., Dance shoes"
                    onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addWhatToBring())}
                  />
                  <Button type="button" onClick={addWhatToBring} size="sm" className="bg-purple-500 hover:bg-purple-600">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                
                {bootcampForm.what_to_bring.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {bootcampForm.what_to_bring.map((item, idx) => (
                      <Badge key={idx} className="bg-green-500/20 text-green-300 px-3 py-1">
                        {item}
                        <button onClick={() => removeWhatToBring(idx)} className="ml-2 hover:text-white">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Guest Teachers */}
              <div className="space-y-4 border-t border-neutral-800 pt-4">
                <h3 className="text-sm font-medium text-purple-400 uppercase tracking-wider">Guest Teachers</h3>
                
                {/* Existing guest teachers */}
                {bootcampForm.guest_teachers.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {bootcampForm.guest_teachers.map((teacher, idx) => (
                      <div key={idx} className="flex items-center gap-3 bg-neutral-900 p-3 rounded-lg">
                        {teacher.image_url ? (
                          <img src={teacher.image_url} alt={teacher.name} className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                            <Star className="w-5 h-5 text-purple-400" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-white">{teacher.name}</p>
                          <p className="text-sm text-white/50">{teacher.role}</p>
                          {teacher.credits?.length > 0 && (
                            <p className="text-xs text-purple-400">{teacher.credits.join(', ')}</p>
                          )}
                        </div>
                        <Button 
                          type="button"
                          size="sm" 
                          variant="ghost" 
                          onClick={() => removeGuestTeacher(idx)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add new guest teacher form */}
                <Card className="bg-neutral-900/50 border-neutral-800">
                  <CardContent className="p-4 space-y-3">
                    <p className="text-sm text-white/70 font-medium">Add Guest Teacher</p>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        value={guestTeacherForm.name}
                        onChange={e => setGuestTeacherForm({...guestTeacherForm, name: e.target.value})}
                        className="bg-neutral-800 border-neutral-700"
                        placeholder="Name"
                      />
                      <Input
                        value={guestTeacherForm.role}
                        onChange={e => setGuestTeacherForm({...guestTeacherForm, role: e.target.value})}
                        className="bg-neutral-800 border-neutral-700"
                        placeholder="Role (e.g., Choreographer)"
                      />
                    </div>
                    
                    <Textarea
                      value={guestTeacherForm.bio}
                      onChange={e => setGuestTeacherForm({...guestTeacherForm, bio: e.target.value})}
                      className="bg-neutral-800 border-neutral-700"
                      placeholder="Short bio..."
                      rows={2}
                    />
                    
                    <Input
                      value={guestTeacherForm.image_url}
                      onChange={e => setGuestTeacherForm({...guestTeacherForm, image_url: e.target.value})}
                      className="bg-neutral-800 border-neutral-700"
                      placeholder="Photo URL (optional)"
                    />
                    
                    <div className="flex gap-2">
                      <Input
                        value={newCredit}
                        onChange={e => setNewCredit(e.target.value)}
                        className="bg-neutral-800 border-neutral-700 flex-1"
                        placeholder="Add credit (e.g., Les Misérables)"
                        onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addCreditToTeacher())}
                      />
                      <Button type="button" onClick={addCreditToTeacher} size="sm" variant="outline" className="border-neutral-700">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {guestTeacherForm.credits.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {guestTeacherForm.credits.map((credit, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {credit}
                            <button 
                              onClick={() => setGuestTeacherForm({
                                ...guestTeacherForm,
                                credits: guestTeacherForm.credits.filter((_, i) => i !== idx)
                              })} 
                              className="ml-1"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    <Button 
                      type="button"
                      onClick={addGuestTeacher} 
                      className="w-full bg-purple-500/20 hover:bg-purple-500/30 text-purple-300"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add Guest Teacher
                    </Button>
                  </CardContent>
                </Card>
              </div>
              
              {/* Daily Schedule */}
              <div className="space-y-4 border-t border-neutral-800 pt-4">
                <h3 className="text-sm font-medium text-purple-400 uppercase tracking-wider">Daily Schedule (Optional)</h3>
                <Textarea
                  value={bootcampForm.daily_schedule}
                  onChange={e => setBootcampForm({...bootcampForm, daily_schedule: e.target.value})}
                  className="bg-neutral-900 border-neutral-700"
                  placeholder="9:00 AM - Warm-up&#10;10:00 AM - Dance class&#10;..."
                  rows={4}
                />
              </div>
            </div>
          </ScrollArea>
          
          <DialogFooter className="border-t border-neutral-800 pt-4">
            <Button variant="outline" onClick={() => setBootcampModal(false)} className="border-neutral-700">
              Cancel
            </Button>
            <Button onClick={handleCreateBootcamp} className="bg-purple-500 hover:bg-purple-600">
              {editingBootcamp ? 'Update' : 'Create'} Bootcamp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Booking Details Modal */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="sm:max-w-md bg-neutral-950 border-neutral-800 text-white">
          <DialogHeader>
            <DialogTitle>Booking Details</DialogTitle>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-white/50">Student Name</p>
                  <p className="font-medium">{selectedBooking.student_name}</p>
                </div>
                <div>
                  <p className="text-sm text-white/50">Age</p>
                  <p className="font-medium">{selectedBooking.student_age}</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-white/50">Bootcamp</p>
                <p className="font-medium">{selectedBooking.bootcamp_name}</p>
              </div>
              
              <div className="border-t border-neutral-800 pt-4">
                <p className="text-sm text-white/50 mb-2">Parent/Guardian</p>
                <p className="font-medium">{selectedBooking.parent_name}</p>
                <p className="text-white/70">{selectedBooking.parent_email}</p>
                <p className="text-white/70">{selectedBooking.parent_phone}</p>
              </div>
              
              {selectedBooking.emergency_contact_name && (
                <div className="border-t border-neutral-800 pt-4">
                  <p className="text-sm text-white/50 mb-2">Emergency Contact</p>
                  <p className="font-medium">{selectedBooking.emergency_contact_name}</p>
                  <p className="text-white/70">{selectedBooking.emergency_contact_phone}</p>
                </div>
              )}
              
              {selectedBooking.medical_info && (
                <div className="border-t border-neutral-800 pt-4">
                  <p className="text-sm text-white/50 mb-2">Medical Information</p>
                  <p className="text-white/70">{selectedBooking.medical_info}</p>
                </div>
              )}
              
              <div className="border-t border-neutral-800 pt-4 flex justify-between items-center">
                <div>
                  <p className="text-sm text-white/50">Amount</p>
                  <p className="text-xl font-bold text-purple-400">£{selectedBooking.amount}</p>
                </div>
                <div>
                  <p className="text-sm text-white/50">Status</p>
                  {getStatusBadge(selectedBooking.status)}
                </div>
              </div>
              
              {selectedBooking.status === 'pending' && (
                <div className="flex gap-2 pt-4">
                  <Button 
                    onClick={() => handleUpdateBookingStatus(selectedBooking.id, 'confirmed')}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Confirm
                  </Button>
                  <Button 
                    onClick={() => handleUpdateBookingStatus(selectedBooking.id, 'cancelled')}
                    variant="outline"
                    className="flex-1 border-red-500 text-red-400 hover:bg-red-500/10"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              )}
              
              {selectedBooking.payment_status !== 'paid' && (
                <div className="pt-2">
                  <Button 
                    onClick={() => handleRequestPayment(selectedBooking)}
                    className="w-full bg-amber-600 hover:bg-amber-700"
                    data-testid="request-payment-detail-btn"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Copy Payment Link
                  </Button>
                  <p className="text-xs text-white/40 text-center mt-2">Creates a Stripe checkout link — send to parent via text/email</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Link Dialog */}
      <Dialog open={!!paymentLink} onOpenChange={() => setPaymentLink(null)}>
        <DialogContent className="bg-[#111] border-[#333] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-amber-400" />
              Payment Link Ready
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-white/60">
              Send this link to <span className="text-white font-medium">{paymentLinkBooking?.parent_name}</span> ({paymentLinkBooking?.parent_email}):
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={paymentLink || ''}
                className="flex-1 bg-[#0a0a0a] border border-[#333] rounded-lg p-3 text-white text-sm font-mono"
                onClick={(e) => e.target.select()}
                data-testid="payment-link-input"
              />
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(paymentLink).then(
                    () => toast.success('Copied!'),
                    () => toast.error('Please copy manually')
                  );
                }}
                className="bg-amber-600 hover:bg-amber-700 shrink-0"
              >
                Copy
              </Button>
            </div>
            <p className="text-xs text-white/40">
              Parent will see: bootcamp details, amount (£{paymentLinkBooking?.amount}), and options to Pay in Full or Pay in 3 Instalments.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentLink(null)} className="border-[#333] text-white">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Reconciliation Modal */}
      <Dialog open={!!reconcileBooking} onOpenChange={(o) => { if (!o) setReconcileBooking(null); }}>
        <DialogContent className="bg-[#111] border-emerald-500/30 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PoundSterling className="w-5 h-5 text-emerald-400" />
              Reconcile Payment — {reconcileBooking?.student_name}
            </DialogTitle>
          </DialogHeader>
          {reconcileBooking && (
            <div className="space-y-3">
              <div className="bg-[#0a0a0a] border border-[#222] rounded-lg p-3 text-sm space-y-1">
                <p><span className="text-white/50">Booking total:</span> <span className="text-white">£{Number(reconcileBooking.amount || 0).toFixed(2)}</span></p>
                <p><span className="text-white/50">Parent:</span> <span className="text-white">{reconcileBooking.parent_email}</span></p>
              </div>

              <div>
                <Label className="text-white/70">Amount paid so far (£)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={reconcileForm.amount_paid}
                  onChange={e => setReconcileForm({...reconcileForm, amount_paid: e.target.value})}
                  className="bg-[#0a0a0a] border-[#333] text-white mt-1"
                  placeholder={`e.g. ${(reconcileBooking.amount || 0).toFixed(2)} or 91.67`}
                  data-testid="reconcile-amount-paid"
                />
                <p className="text-xs text-white/40 mt-1">
                  Will auto-set status to <strong>Paid in full</strong> when ≥ £{Number(reconcileBooking.amount || 0).toFixed(2)}, otherwise <strong>Partial</strong>.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-white/70 text-xs">Total instalments</Label>
                  <Input
                    type="number" min="0"
                    value={reconcileForm.installment_total}
                    onChange={e => setReconcileForm({...reconcileForm, installment_total: e.target.value})}
                    className="bg-[#0a0a0a] border-[#333] text-white mt-1"
                    placeholder="e.g. 3"
                  />
                </div>
                <div>
                  <Label className="text-white/70 text-xs">Instalments received</Label>
                  <Input
                    type="number" min="0"
                    value={reconcileForm.installments_paid}
                    onChange={e => setReconcileForm({...reconcileForm, installments_paid: e.target.value})}
                    className="bg-[#0a0a0a] border-[#333] text-white mt-1"
                    placeholder="e.g. 1"
                  />
                </div>
                <div>
                  <Label className="text-white/70 text-xs">Next instalment date</Label>
                  <Input
                    type="date"
                    value={reconcileForm.next_installment_date}
                    onChange={e => setReconcileForm({...reconcileForm, next_installment_date: e.target.value})}
                    className="bg-[#0a0a0a] border-[#333] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white/70 text-xs">Next instalment £</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={reconcileForm.next_installment_amount}
                    onChange={e => setReconcileForm({...reconcileForm, next_installment_amount: e.target.value})}
                    className="bg-[#0a0a0a] border-[#333] text-white mt-1"
                    placeholder="e.g. 91.67"
                  />
                </div>
              </div>

              <div>
                <Label className="text-white/70">Stripe PaymentIntent ID <span className="text-white/40">(optional)</span></Label>
                <Input
                  value={reconcileForm.stripe_payment_intent_id}
                  onChange={e => setReconcileForm({...reconcileForm, stripe_payment_intent_id: e.target.value})}
                  className="bg-[#0a0a0a] border-[#333] text-white mt-1 font-mono text-xs"
                  placeholder="pi_3T... (paste from Stripe dashboard for click-through)"
                  data-testid="reconcile-pi"
                />
              </div>

              <div>
                <Label className="text-white/70">Note <span className="text-white/40">(audit trail)</span></Label>
                <Input
                  value={reconcileForm.note}
                  onChange={e => setReconcileForm({...reconcileForm, note: e.target.value})}
                  className="bg-[#0a0a0a] border-[#333] text-white mt-1"
                  placeholder="e.g. paid in full via Stripe link 4 May, or 1st of 3 instalments"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReconcileBooking(null)} className="border-[#333] text-white">Cancel</Button>
            <Button
              onClick={submitReconcile}
              disabled={reconcileSaving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="reconcile-submit"
            >
              {reconcileSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : 'Save reconciliation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Sibling Modal */}
      <Dialog open={!!siblingFor} onOpenChange={(o) => { if (!o) setSiblingFor(null); }}>
        <DialogContent className="bg-[#111] border-pink-500/30 text-white max-w-md" data-testid="add-sibling-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-pink-400" />
              Add Sibling — {siblingFor?.student_name}
            </DialogTitle>
          </DialogHeader>
          {siblingFor && (
            <div className="space-y-3">
              <div className="bg-[#0a0a0a] border border-[#222] rounded-lg p-3 text-sm space-y-1">
                <p><span className="text-white/50">Camp:</span> <span className="text-white">{siblingFor.bootcamp_name}</span></p>
                <p><span className="text-white/50">Parent:</span> <span className="text-white">{siblingFor.parent_email}</span></p>
                <p>
                  <span className="text-white/50">Existing booking status:</span>{' '}
                  <span className={siblingFor.payment_status === 'paid' ? 'text-emerald-400' : 'text-amber-400'}>
                    {siblingFor.payment_status || 'pending'}
                  </span>
                </p>
                <p className="text-pink-400 text-xs pt-1">
                  {siblingFor.payment_status === 'paid'
                    ? '✓ 30% sibling discount will apply automatically'
                    : '⚠ Full price (sibling needs to pay first; reconciling the first booking will retroactively apply 30%).'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-white/70 text-xs">Student name *</Label>
                  <Input
                    value={siblingForm.student_name}
                    onChange={(e) => setSiblingForm({ ...siblingForm, student_name: e.target.value })}
                    className="bg-[#0a0a0a] border-[#333] text-white mt-1"
                    placeholder="Full name"
                    data-testid="sibling-name-input"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-white/70 text-xs">Age *</Label>
                  <Input
                    type="number" min="1" max="99"
                    value={siblingForm.student_age}
                    onChange={(e) => setSiblingForm({ ...siblingForm, student_age: e.target.value })}
                    className="bg-[#0a0a0a] border-[#333] text-white mt-1"
                    placeholder="e.g. 12"
                    data-testid="sibling-age-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-white/70 text-xs">Medical / Allergies</Label>
                <Textarea
                  value={siblingForm.medical_info}
                  onChange={(e) => setSiblingForm({ ...siblingForm, medical_info: e.target.value })}
                  className="bg-[#0a0a0a] border-[#333] text-white mt-1 min-h-[60px]"
                  placeholder="Any allergies, medications…"
                />
              </div>

              <div className="border-t border-[#222] pt-3 space-y-2">
                <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={siblingForm.mark_as_paid}
                    onChange={(e) => setSiblingForm({ ...siblingForm, mark_as_paid: e.target.checked })}
                    className="accent-emerald-500"
                    data-testid="sibling-mark-paid"
                  />
                  Mark as already paid (no Stripe charge)
                </label>
                {siblingForm.mark_as_paid && (
                  <>
                    <div>
                      <Label className="text-white/70 text-xs">Amount paid (£) — optional, defaults to discounted price</Label>
                      <Input
                        type="number" step="0.01" min="0"
                        value={siblingForm.paid_amount}
                        onChange={(e) => setSiblingForm({ ...siblingForm, paid_amount: e.target.value })}
                        className="bg-[#0a0a0a] border-[#333] text-white mt-1"
                        placeholder="Leave blank to use auto-discounted amount"
                      />
                    </div>
                    <div>
                      <Label className="text-white/70 text-xs">Note</Label>
                      <Input
                        value={siblingForm.paid_note}
                        onChange={(e) => setSiblingForm({ ...siblingForm, paid_note: e.target.value })}
                        className="bg-[#0a0a0a] border-[#333] text-white mt-1"
                        placeholder="e.g. paid via bank transfer"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setSiblingFor(null)} className="border-[#333] text-white">Cancel</Button>
            <Button
              onClick={handleAddSibling}
              disabled={siblingSaving}
              className="bg-pink-600 hover:bg-pink-700"
              data-testid="sibling-submit"
            >
              {siblingSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Adding…</> : 'Add sibling'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ManualBootcampBookingModal
        open={manualBookOpen}
        onClose={() => setManualBookOpen(false)}
        bootcamps={bootcamps}
        onCreated={() => { fetchData({ silent: true }); }}
      />
    </div>
  );
};

export default BootcampAdmin;
